import { $playerConfig, $playerProfile } from '../../backend/store';
import Session from '../game/session/Session';
import Spinner from './spinner/Spinner';

const BASE_URL: string = 'https://alice-bob2.hackney.games/assets';
const BG_PATH: string = 'images/bg';
const MOBS_PATH: string = 'spine/mobs';
const STORY_PATH: string = 'images/story';

type ConfigChapter = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export default class Lazyloader extends Phaser.Scene {
    layers!: Record<string, Phaser.GameObjects.Container>;
    spinner!: Spinner;

    constructor() {
        super('Lazyloader');
    }

    create() {
        this.events.once('update', this.start, this);
    }

    async start() {
        this.setLayers();

        this.spinner = new Spinner(this, this.scale.width / 2, this.scale.height / 2);
        this.layers.bg.add(this.spinner);
        this.spinner.hide();

        this.setupListeners();
    }

    setupListeners(): void {
        this.events.once('shutdown', this.destroy, this);
        this.game.events.on('LOAD_CHAPTER_ASSETS', this.loadChapterAssets, this);
        this.game.events.on('LOAD_STORY_ASSETS', this.loadStoryAssets, this);
    }

    async loadChapterAssets(): Promise<void> {
        let chapter = $playerProfile.getState().current_chapter

        if (chapter === 1) {
            this.game.events.emit('CHAPTER_ASSETS_LOADED');
            return;
        }

        const manifest = this.buildManifest(chapter);
        this.loadAssets(manifest);
    }

    private loadAssets(manifest: any): void {
        this.spinner.show();

        this.load.setBaseURL(BASE_URL);
        this.load.setPath(MOBS_PATH);

        for (let mob of manifest.mobs) {
            if (!mob) continue;
            if (mob == 'Card_2') this.load.spine(mob, `Card_1.json`, `Card_1.atlas`);
            else if (mob == 'Card_4') this.load.spine(mob, `Card_3.json`, `Card_3.atlas`);
            else if (mob == 'Caterpillar') {
                this.load.spine(mob, `caterpillar/Boss/Boss.json`, `caterpillar/Boss/Boss.atlas`);
                this.load.spine(mob + `_Smoke1`, `caterpillar/Smoke1/Boss.json`, `caterpillar/Smoke1/Boss.atlas`);
                this.load.spine(mob + `_Smoke2`, `caterpillar/Smoke2/Boss.json`, `caterpillar/Smoke2/Boss.atlas`);
            }
            else if (mob == 'Key_1') this.load.spine(mob, `Key_1/Key_1.json`, `Key_1/Key_1.atlas`);
            else if (mob == 'Key_2') this.load.spine(mob, `Key_1/Key_1.json`, `Key_1/Key_1.atlas`);
            else if (mob == 'Key_3') this.load.spine(mob, `Key_2/Key_2.json`, `Key_2/Key_2.atlas`);
            else if (mob == 'Key_4') this.load.spine(mob, `Key_2/Key_2.json`, `Key_2/Key_2.atlas`);
            else if (mob == 'Key_5') this.load.spine(mob, `Key_3/Key_3.json`, `Key_3/Key_3.atlas`);
            else if (mob == 'Key_6') this.load.spine(mob, `Key_3/Key_3.json`, `Key_3/Key_3.atlas`);
            else this.load.spine(mob, `${mob}.json`, `${mob}.atlas`);
        }

        this.load.setPath(BG_PATH);
        this.load.image(manifest.bg, `${manifest.bg}.png`);

        this.load.once('complete', () => {
            this.game.events.emit('CHAPTER_ASSETS_LOADED');
            this.spinner.hide();
        });

        if (this.load.list.size > 0) {
            this.load.start();
        } else {
            this.game.events.emit('CHAPTER_ASSETS_LOADED');
            this.spinner.hide();
        }
    }

    private loadStoryAssets(chapterId: number): void {
        const manifest = [
            {
                chapterId: 1,
                pages: [0, 1, 2, 3, 4, 5, 6],
            },
            {
                chapterId: 2,
                pages: [1, 2, 3, 4, 5, 6],
            },
            {
                chapterId: 3,
                pages: [1, 2, 3, 4, 5, 6, 7],
            },
            {
                chapterId: 4,
                pages: [1, 2, 3, 4, 5, 6],
            },
            {
                chapterId: 5,
                pages: [1, 2, 3, 4, 5, 6],
            },
            {
                chapterId: 6,
                pages: [1, 2, 3, 4, 5, 6, 7],
            },

        ];

        this.spinner.show();

        this.load.setBaseURL(BASE_URL);
        this.load.setPath(`${STORY_PATH}/chapter_${chapterId}`);

        let keys = [];
        let paths = [];
        let source = manifest.find((entry) => entry.chapterId === chapterId)!;
        if (!source) throw new Error(`pages not found for chapter ${chapterId}`);

        for (let page of source.pages) {
            keys.push(`comic/chapter_${chapterId}/${chapterId}-${page}`);
            paths.push(`${chapterId}-${page}.webp`);
        }

        for (let i = 0; i < keys.length; i++) {
            this.load.image(keys[i], paths[i]);
        }

        this.load.once('complete', () => {
            this.game.events.emit('STORY_ASSETS_LOADED');
            this.spinner.hide();
        });

        if (this.load.list.size > 0) {
            this.load.start();
        } else {
            this.game.events.emit('STORY_ASSETS_LOADED');
            this.spinner.hide();
        }
    }

    setLayers() {
        const names: string[] = ['bg'];
        this.layers = {} as Record<string, Phaser.GameObjects.Container>;

        names.forEach((name) => {
            this.layers[name] = this.add.container(0, 0);
        });
    }

    private buildManifest(chapterId: ConfigChapter): any {
        const manifest: { mobs: string[]; bg: string } = { mobs: [], bg: '' };

        manifest.bg = $playerConfig.getState().chapters[chapterId].chapter_background;
        const enemies = $playerConfig.getState().enemies[chapterId];

        Object.values(enemies).forEach((mob) => {
            manifest.mobs.push(mob.enemy_name);
        });

        return manifest;
    }

    destroy() { }
}
